/**
 * Advanced Pricing Calculator
 * 
 * Implements all complex pricing formulas from Google Sheets "Opties" tab
 * Handles non-standard dimensions with proper rounding rules
 */

export class PricingCalculator {
    constructor(optiesData) {
        this.optiesData = optiesData;
        this.parseOptiesData();
    }

    /**
     * Parse Opties data from Google Sheets into usable format
     */
    parseOptiesData() {
        if (!this.optiesData) return;

        this.opties = {};
        
        for (const optie of this.optiesData) {
            const name = optie.Optie;
            
            // Try both with and without space for PrijsPerStuk (CSV header inconsistency)
            const prijsPerStuk = this.parsePrice(optie[' PrijsPerStuk'] || optie['PrijsPerStuk']);
            
            this.opties[name] = {
                perZijde: optie.PerZijde === 'ja',
                perM2: optie.PerM2 === 'ja',
                perM: optie.PerM === 'ja',
                prijsPerStuk: prijsPerStuk,
                prijsPerM2: this.parsePrice(optie.PrijsPerM2),
                prijsPerM: this.parsePrice(optie.PrijsPerM),
                prijsOpAfmeting: this.parsePricingTable(optie.PrijsOpAfmeting),
                info: optie['Info berekening']
            };
        }
    }

    /**
     * Parse price string to number
     * "€ 390,00" -> 390
     */
    parsePrice(priceStr) {
        if (!priceStr || priceStr === '€ 0,00') return 0;
        
        const cleaned = priceStr
            .replace(/€/g, '')
            .replace(/\./g, '')
            .replace(/,/g, '.')
            .trim();
        
        return parseFloat(cleaned) || 0;
    }

    /**
     * Parse pricing table from string
     * "2,0m=520\n2.5m=710\n..." -> { 2.0: 520, 2.5: 710, ... }
     */
    parsePricingTable(tableStr) {
        if (!tableStr || tableStr === '€ 0,00') return {};
        
        const table = {};
        const lines = tableStr.split('\n');
        
        for (const line of lines) {
            const match = line.match(/([0-9.,]+)m?=([0-9]+)/);
            if (match) {
                const meter = parseFloat(match[1].replace(',', '.'));
                const price = parseInt(match[2]);
                table[meter] = price;
            }
        }
        
        return table;
    }

    /**
     * Calculate inkorten (shortening) cost
     */
    calculateInkorten(actualWidth, actualDepth) {
        const inkorten = this.opties['Inkorten'];
        if (!inkorten) return { cost: 0, details: [] };

        const details = [];
        let totalCost = 0;

        // Check width inkorten
        const widthDecimal = actualWidth % 1;
        if (widthDecimal > 0 && widthDecimal < 1) {
            totalCost += inkorten.prijsPerStuk;
            details.push(`Inkorten breedte (${actualWidth}m → ${Math.ceil(actualWidth)}m)`);
        }

        // Check depth inkorten
        const depthDecimal = actualDepth % 1;
        if (depthDecimal > 0 && depthDecimal < 1) {
            // Depth uses half-meter increments
            const remainder = actualDepth % 0.5;
            if (remainder > 0) {
                totalCost += inkorten.prijsPerStuk;
                const roundedDepth = Math.ceil(actualDepth / 0.5) * 0.5;
                details.push(`Inkorten diepte (${actualDepth}m → ${roundedDepth}m)`);
            }
        }

        return {
            cost: totalCost,
            details: details,
            count: details.length
        };
    }

    /**
     * Calculate LED lighting cost
     * Formula: (Math.ceil(width) + 1 - 2) * 2 * €25
     */
    calculateLED(width, isEnabled) {
        if (!isEnabled) return { cost: 0, details: [] };

        const led = this.opties['Ledverlichting'];
        if (!led) return { cost: 0, details: [] };

        // Step 1: Calculate number of liggers (beams)
        const liggers = Math.ceil(width) + 1;
        
        // Step 2: Exclude outer 2 liggers
        const ledLiggers = Math.max(0, liggers - 2);
        
        // Step 3: 2 LED units per ligger
        const totalLEDs = ledLiggers * 2;
        
        // Step 4: Calculate cost
        const cost = totalLEDs * led.prijsPerStuk;

        return {
            cost: cost,
            details: [`LED verlichting: ${totalLEDs} stuks (${ledLiggers} liggers × 2)`],
            leds: totalLEDs,
            liggers: ledLiggers
        };
    }

    /**
     * Calculate glasdak (glass roof) cost
     * Formula: width * depth * €140/m²
     */
    calculateGlasdak(width, depth, isEnabled) {
        if (!isEnabled) return { cost: 0, details: [] };

        const glasdak = this.opties['Glasdak'];
        if (!glasdak) return { cost: 0, details: [] };

        const area = width * depth;
        const cost = area * glasdak.prijsPerM2;

        return {
            cost: cost,
            details: [`Glasdak: ${area.toFixed(2)}m² × €${glasdak.prijsPerM2}/m²`],
            area: area
        };
    }

    /**
     * Calculate schuifdeuren (sliding doors) cost
     * Round up to whole meters per side
     * leftWall/rightWall: 1 = glaswand met spies
     * frontWall: 1 = glaswand
     */
    calculateSchuifdeuren(width, depth, frontWall, leftWall, rightWall) {
        const schuif = this.opties['Schuifdeuren'];
        if (!schuif) return { cost: 0, details: [] };

        let totalCost = 0;
        const details = [];

        // Front wall (uses width) - value 1 = glaswand
        if (frontWall === '1') {
            const meters = Math.ceil(width);
            const cost = meters * schuif.prijsPerM;
            totalCost += cost;
            details.push(`Schuifdeuren voor: ${meters}m × €${schuif.prijsPerM}/m`);
        }

        // Left wall (uses depth) - value 1 = glaswand met spies
        if (leftWall === '1') {
            const meters = Math.ceil(depth);
            const cost = meters * schuif.prijsPerM;
            totalCost += cost;
            details.push(`Schuifdeuren links: ${meters}m × €${schuif.prijsPerM}/m`);
        }

        // Right wall (uses depth) - value 1 = glaswand met spies
        if (rightWall === '1') {
            const meters = Math.ceil(depth);
            const cost = meters * schuif.prijsPerM;
            totalCost += cost;
            details.push(`Schuifdeuren rechts: ${meters}m × €${schuif.prijsPerM}/m`);
        }

        return {
            cost: totalCost,
            details: details
        };
    }

    /**
     * Calculate lambrisering (aluminum wall) cost
     * Uses depth with rounding to available sizes
     * leftWall/rightWall: 2 = aluminiumwand met spies
     */
    calculateLambrisering(depth, leftWall, rightWall) {
        const lambr = this.opties['LambriseringAluminiumWand'];
        if (!lambr || !lambr.prijsOpAfmeting) return { cost: 0, details: [] };

        let totalCost = 0;
        const details = [];

        // Find appropriate price for depth
        const roundedDepth = this.roundToAvailableSize(depth, lambr.prijsOpAfmeting);
        const pricePerSide = lambr.prijsOpAfmeting[roundedDepth] || 0;

        // Left wall - value 2 = aluminiumwand met spies
        if (leftWall === '2') {
            totalCost += pricePerSide;
            details.push(`Lambrisering links (${depth}m → ${roundedDepth}m)`);
        }

        // Right wall - value 2 = aluminiumwand met spies
        if (rightWall === '2') {
            totalCost += pricePerSide;
            details.push(`Lambrisering rechts (${depth}m → ${roundedDepth}m)`);
        }

        return {
            cost: totalCost,
            details: details,
            pricePerSide: pricePerSide
        };
    }

    /**
     * Calculate spie cost
     * Uses depth with rounding to available sizes
     * Automatically added when:
     * - leftWall/rightWall: 1 = glaswand met spies
     * - leftWall/rightWall: 2 = aluminiumwand met spies  
     * - leftWall/rightWall: 3 = alleen spies
     */
    calculateSpie(depth, leftWall, rightWall) {
        const spie = this.opties['Spie'];
        if (!spie || !spie.prijsOpAfmeting) {
            return { cost: 0, details: [] };
        }


        let totalCost = 0;
        const details = [];

        // Find appropriate price for depth
        const roundedDepth = this.roundToAvailableSize(depth, spie.prijsOpAfmeting);
        const pricePerSide = spie.prijsOpAfmeting[roundedDepth] || 0;


        // Check if spie needed for left wall
        // 1 = glaswand met spies, 2 = aluminiumwand met spies, 3 = alleen spies
        const needSpieLeft = leftWall === '1' || leftWall === '2' || leftWall === '3';
        
        // Check if spie needed for right wall
        const needSpieRight = rightWall === '1' || rightWall === '2' || rightWall === '3';


        if (needSpieLeft) {
            totalCost += pricePerSide;
            details.push(`Spie links (${depth}m → ${roundedDepth}m)`);
        }

        if (needSpieRight) {
            totalCost += pricePerSide;
            details.push(`Spie rechts (${depth}m → ${roundedDepth}m)`);
        }

        return {
            cost: totalCost,
            details: details,
            pricePerSide: pricePerSide
        };
    }

    /**
     * Round dimension to nearest available size in pricing table
     * Always rounds UP
     */
    roundToAvailableSize(dimension, pricingTable) {
        const sizes = Object.keys(pricingTable).map(parseFloat).sort((a, b) => a - b);
        
        
        for (const size of sizes) {
            if (dimension <= size) {
                return size;
            }
        }
        
        // If larger than all available, return largest
        const largest = sizes[sizes.length - 1];
        return largest;
    }

    /**
     * Get rounded dimensions for base price lookup
     */
    getRoundedDimensions(actualWidth, actualDepth) {
        // Width: round up to whole meter if has decimal
        let roundedWidth = actualWidth;
        if (actualWidth % 1 > 0) {
            roundedWidth = Math.ceil(actualWidth);
        }

        // Depth: round to nearest half meter
        let roundedDepth = actualDepth;
        if (actualDepth % 0.5 > 0) {
            roundedDepth = Math.ceil(actualDepth / 0.5) * 0.5;
        }

        return {
            width: roundedWidth,
            depth: roundedDepth
        };
    }

    /**
     * Calculate complete pricing breakdown
     */
    calculate(config) {
        const {
            width,
            depth,
            type, // 'standaard' or 'pergamon'
            covering, // '1' = poly, '2' = glasdak
            led, // '1' = yes
            leftWall,
            rightWall,
            frontWall
        } = config;


        const breakdown = {
            base: { cost: 0, details: [] },
            inkorten: { cost: 0, details: [] },
            led: { cost: 0, details: [] },
            glasdak: { cost: 0, details: [] },
            schuifdeuren: { cost: 0, details: [] },
            lambrisering: { cost: 0, details: [] },
            spie: { cost: 0, details: [] },
            total: 0,
            actualDimensions: { width, depth },
            roundedDimensions: this.getRoundedDimensions(width, depth)
        };


        // Calculate inkorten
        breakdown.inkorten = this.calculateInkorten(width, depth);

        // Calculate LED
        breakdown.led = this.calculateLED(width, led === '1');

        // Calculate glasdak
        breakdown.glasdak = this.calculateGlasdak(width, depth, covering === '2');

        // Calculate schuifdeuren
        breakdown.schuifdeuren = this.calculateSchuifdeuren(width, depth, frontWall, leftWall, rightWall);

        // Calculate lambrisering
        breakdown.lambrisering = this.calculateLambrisering(depth, leftWall, rightWall);

        // Calculate spie (auto-includes when needed)
        breakdown.spie = this.calculateSpie(depth, leftWall, rightWall);

        // Calculate total extras
        const totalExtras = 
            breakdown.inkorten.cost +
            breakdown.led.cost +
            breakdown.glasdak.cost +
            breakdown.schuifdeuren.cost +
            breakdown.lambrisering.cost +
            breakdown.spie.cost;


        return {
            ...breakdown,
            totalExtras: totalExtras
        };
    }

    /**
     * Format price for display
     */
    formatPrice(price) {
        if (!price) return '0,00';
        
        const parts = price.toFixed(2).split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];
        
        const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        
        return `${formattedInteger},${decimalPart}`;
    }
}

export default PricingCalculator;
